import React, { useEffect, useRef, useState } from 'react';
import { AttentionItem } from '../hooks/useAttentionItems';

interface AttentionRowProps {
  item: AttentionItem;
  onClick: () => void;
  /** Optional — when provided, a [× dismiss] button appears on hover. */
  onDismiss?: (item: AttentionItem) => void;
  /** Per-kind dismiss tooltip, so users see the actual effect on hover. */
  dismissHint?: string;
  /**
   * Optional — when provided, a [push ▾] dropdown appears on hover with
   * preset options (7/30/365 days). Only wired for overdue_job rows today.
   */
  onPushOut?: (item: AttentionItem, days: number) => void;
}

/**
 * Single row in the Attention Queue. Rendered as:
 *   [OVERDUE] smith-construction         inv 2034 · $4,230 · 12d late   [open ⏎] [×]
 *
 * The status code color follows the item's category. Hitting Enter on a
 * focused row, or clicking anywhere on it, triggers the primary action.
 * Clicking the dismiss button stops propagation and fires onDismiss instead.
 */
const AttentionRow: React.FC<AttentionRowProps> = ({ item, onClick, onDismiss, dismissHint, onPushOut }) => {
  const [pushOpen, setPushOpen] = useState(false);
  const pushRef = useRef<HTMLDivElement>(null);

  // Close the push menu on outside click or Escape
  useEffect(() => {
    if (!pushOpen) return;
    const handleMouse = (e: MouseEvent) => {
      if (!pushRef.current?.contains(e.target as Node)) setPushOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPushOpen(false);
    };
    document.addEventListener('mousedown', handleMouse);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleMouse);
      document.removeEventListener('keydown', handleKey);
    };
  }, [pushOpen]);

  const handleKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Don't swallow Enter/Space if the focus is inside the push menu.
    if (pushOpen) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  const handleDismissClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); // don't let it bubble to the row and fire onClick
    if (onDismiss) onDismiss(item);
  };

  const handlePushToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setPushOpen(o => !o);
  };

  const handlePushSelect = (e: React.MouseEvent<HTMLButtonElement>, days: number) => {
    e.stopPropagation();
    setPushOpen(false);
    if (onPushOut) onPushOut(item, days);
  };

  const statusClass = `home-row__code home-row__code--${item.statusCode.toLowerCase()}`;

  return (
    <div
      className="home-row"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKey}
      aria-label={`${item.statusCode} ${item.title}. ${item.subtitle}. Press Enter to ${item.actionLabel}.`}
    >
      <span className={statusClass}>[{item.statusCode}]</span>
      <span className="home-row__title">{item.title}</span>
      <span className="home-row__sep">·</span>
      <span className="home-row__subtitle">{item.subtitle}</span>
      <span className="home-row__action">
        [{item.actionLabel} <span className="home-row__enter-glyph">⏎</span>]
      </span>

      {onPushOut && (
        <div className="home-row__push" ref={pushRef}>
          <button
            type="button"
            className="home-row__push-toggle"
            onClick={handlePushToggle}
            title="Push due date out"
            aria-label="Push due date out"
            aria-haspopup="menu"
            aria-expanded={pushOpen}
          >
            [push {pushOpen ? '▴' : '▾'}]
          </button>
          {pushOpen && (
            <div className="home-row__push-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="home-row__push-item"
                onClick={(e) => handlePushSelect(e, 7)}
              >
                +7 days
              </button>
              <button
                type="button"
                role="menuitem"
                className="home-row__push-item"
                onClick={(e) => handlePushSelect(e, 30)}
              >
                +30 days
              </button>
              <button
                type="button"
                role="menuitem"
                className="home-row__push-item"
                onClick={(e) => handlePushSelect(e, 365)}
              >
                +1 year
              </button>
            </div>
          )}
        </div>
      )}

      {onDismiss && (
        <button
          type="button"
          className="home-row__dismiss"
          onClick={handleDismissClick}
          title={dismissHint || 'Dismiss'}
          aria-label={dismissHint || 'Dismiss'}
        >
          [×]
        </button>
      )}
    </div>
  );
};

export default AttentionRow;
